import { useMemo } from 'react';
import { MemberType, ProjectId, Role, Team, User } from 'api/models';
import { useRolesWithPlaceholderQuery } from 'queries/roles';
import { OptionGroup } from '../components/organisms/field-editors/components/controlled-select-based-editor-popover';
import {
  useGenerateAssigneePickerOptions,
  useGetAssignableTeamsForProject,
} from './use-assignee-options';
import {
  useGetAllTeamsFromIndexedDb,
  useMembersInAndNotInProjects,
} from './use-idb';

export function useSelectedProjectsOptions({
  projectIds,
  isPrivate = false,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): OptionGroup<string>[] {
  const { data: roles } = useRolesWithPlaceholderQuery();
  const teams = useGetAllTeamsFromIndexedDb();

  const { inProjectUsers, notInProjectUsers } = useMembersInAndNotInProjects({
    projectIds,
    isPrivate,
  });
  const isSingleProjectSelected = projectIds.length === 1;

  const teamsFromSingleProject = useGetAssignableTeamsForProject({
    projectId: isSingleProjectSelected ? ProjectId(projectIds[0]) : undefined,
    isPrivate,
  });

  const teamsInProjects = useMemo(() => {
    return isSingleProjectSelected
      ? teamsFromSingleProject
      : teams.filter((team) => {
          if (isPrivate === true && team.teamType === MemberType.External) {
            return false;
          }
          if (team.accountLevelTeam) {
            return true;
          }

          return isSingleProjectSelected
            ? team.projectIds?.includes(projectIds[0])
            : false;
        });
  }, [
    isPrivate,
    isSingleProjectSelected,
    projectIds,
    teams,
    teamsFromSingleProject,
  ]);
  const { generateAssigneePickerOptions } =
    useGenerateAssigneePickerOptions(true);

  return useMemo(
    () =>
      generateAssigneePickerOptions({
        users: inProjectUsers as User[],
        teams: Array.isArray(teamsInProjects)
          ? teamsInProjects
          : ([] as Team[]),
        usersNotInProject: notInProjectUsers,
        roles: roles as Role[],
      }),
    [
      generateAssigneePickerOptions,
      inProjectUsers,
      notInProjectUsers,
      roles,
      teamsInProjects,
    ],
  );
}
